import React, { useEffect, useMemo, useState } from 'react';
import Select from 'react-select';
import { db, auth } from '../firebaseConfig';
import {
  doc,
  setDoc,
  getDoc,
  updateDoc,
  serverTimestamp,
  Timestamp
} from 'firebase/firestore';
import { useNavigate } from 'react-router-dom';
import '../styles/CreateTeam.css';

const REGION_OPTIONS = [
  { value: 'NA', label: 'North America' },
  { value: 'LATAM', label: 'Latin America' },
  { value: 'EU', label: 'Europe' },
  { value: 'APAC', label: 'APAC' },
  { value: 'MEA', label: 'Middle East & Africa' }
];

const CreateTeam = () => {
  const [step, setStep] = useState(1);
  const [teamName, setTeamName] = useState('');
  const [role, setRole] = useState('player'); // Default to player - can be changed in Settings later
  const [region, setRegion] = useState(null);
  const [teamLogo, setTeamLogo] = useState(null);
  const [previewLogo, setPreviewLogo] = useState(null);
  const [teamId, setTeamId] = useState(null);
  const [inviteCode, setInviteCode] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    // Auto-detect region best-effort
    if (!region) {
      const tz = Intl.DateTimeFormat().resolvedOptions().timeZone || '';
      if (tz.includes('America')) setRegion(REGION_OPTIONS[0]);
      else if (tz.includes('Europe')) setRegion(REGION_OPTIONS[2]);
      else if (tz.includes('Asia') || tz.includes('Pacific')) setRegion(REGION_OPTIONS[3]);
      else if (tz.includes('Africa') || tz.includes('Dubai') || tz.includes('Riyadh')) setRegion(REGION_OPTIONS[4]);
      else setRegion(REGION_OPTIONS[0]);
    }
  }, [region]);

  const generateTeamIdFromName = (name) =>
    name.toLowerCase().trim().replace(/\s+/g, '_');

  const generateInviteCode = () =>
    Math.random().toString(36).substring(2, 10);

  const handleLogoUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      setTeamLogo(reader.result);
      setPreviewLogo(reader.result);
    };
    reader.readAsDataURL(file);
  };

  const canNext = useMemo(() => teamName.trim().length >= 3, [teamName]);
  const canCreate = useMemo(() => !!region && canNext, [region, canNext]);

  const createTeam = async () => {
    if (!canCreate) return;
    setError('');
    const user = auth?.currentUser;
    if (!user) {
      setError('You must be logged in.');
      return;
    }

    const teamIdGenerated = generateTeamIdFromName(teamName);
    const inviteCodeGenerated = generateInviteCode();
    const expiresAt = Timestamp.fromDate(new Date(Date.now() + 24 * 60 * 60 * 1000)); // 24 hours

    setLoading(true);
    try {
      const teamRef = doc(db, `teams/${teamIdGenerated}`);
      const existing = await getDoc(teamRef);
      if (existing.exists()) {
        setError(`A team with code "${teamIdGenerated}" already exists. Try another name.`);
        setLoading(false);
        return;
      }

      const seasonYear = new Date().getFullYear();
      await setDoc(teamRef, {
        name: teamName.trim(),
        coachId: user.uid,
        memberIds: [user.uid],
        owner: user.uid,
        createdAt: serverTimestamp(),
        logo: teamLogo || null,
        inviteCode: inviteCodeGenerated,
        inviteCodeExpiresAt: expiresAt,
        region: region.value,
        currentSeasonYear: seasonYear,
        status: 'request'
      });

      await updateDoc(doc(db, `users/${user.uid}`), {
        teamId: teamIdGenerated,
        role
      });

      // Publish to public-safe team directory so players can discover/join
      await setDoc(doc(db, 'teamDirectory', teamIdGenerated), {
        name: teamName.trim(),
        region: region.value,
        season: String(seasonYear),
        league: 'Open',
        members: 1,
        capacity: 20,
        status: 'request',
        description: '',
        ownerUid: user.uid,
        createdAt: serverTimestamp(),
      });

      setTeamId(teamIdGenerated);
      setInviteCode(inviteCodeGenerated);
      
      // Mark that user should see tour (they haven't completed it yet)
      // The tour will trigger when they land on /my-strategy
    } catch (err) {
      console.error('❌ Error creating team:', err.message);
      setError('Something went wrong: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = () => {
    if (!inviteCode) return;
    navigator.clipboard.writeText(inviteCode);
  };

  const goToDashboard = () => navigate('/my-strategy');

  return (
    <div className="create-wrapper">
      <div className="wizard-card">
        <div className="wizard-header">
          <div>
            <p className="eyebrow">Create your team — ~90s</p>
            <h1>Step {step} of 2</h1>
            <p className="subhead">You can edit details later. Core features stay free.</p>
          </div>
          <div className="progress">
            <div className="progress-bar" style={{ width: `${step === 1 ? 50 : 100}%` }} />
          </div>
        </div>

        {error && <div className="error-banner" role="alert">{error}</div>}

        {step === 1 && (
          <div className="wizard-body">
            <label className="field">
              <span>Team name</span>
              <div className="input-row">
                <input
                  type="text"
                  maxLength={30}
                  value={teamName}
                  onChange={(e) => setTeamName(e.target.value)}
                  placeholder="e.g., Alpha Wolves"
                />
                <span className="counter">{teamName.length}/30</span>
              </div>
            </label>

            <p className="field-hint">
              Your role will be set to "Player" by default. You can change it later in Settings.
            </p>
          </div>
        )}

        {step === 2 && (
          <div className="wizard-body two-col">
            <div className="main-col">
              <label className="field">
                <span>Region</span>
                <Select
                  classNamePrefix="react-select"
                  options={REGION_OPTIONS}
                  placeholder="Choose region"
                  value={region}
                  onChange={setRegion}
                />
              </label>

              <label className="field">
                <span>Team logo (optional)</span>
                <input type="file" accept="image/*" onChange={handleLogoUpload} />
              </label>

              {previewLogo && (
                <div className="logo-preview-card">
                  <img src={previewLogo} alt="Team logo preview" />
                  <div>
                    <p className="preview-name">{teamName || 'Your Team'}</p>
                    <p className="preview-sub">Call of Duty: Mobile</p>
                  </div>
                </div>
              )}
            </div>

            <div className="side-card">
              <p className="side-title">What you'll unlock</p>
              <ul>
                <li>Visual Strategy Room — Draw plays and routes</li>
                <li>Playbook Library — Organize strats by map/mode</li>
                <li>Team Calendar — Schedule scrims and reviews</li>
                <li>Stats & Scouting — Track team and opponent data</li>
              </ul>
              <p className="side-note">Core features stay free. Upgrade later.</p>
            </div>
          </div>
        )}

        <div className="wizard-footer">
          {step > 1 && (
            <button className="ghost" onClick={() => setStep(step - 1)} disabled={loading}>
              Back
            </button>
          )}
          {step === 1 && (
            <button className="primary" onClick={() => setStep(2)} disabled={!canNext}>
              Next: Details
            </button>
          )}
          {step === 2 && (
            <button className="primary" onClick={createTeam} disabled={!canCreate || loading}>
              {loading ? 'Creating...' : 'Create team'}
            </button>
          )}
        </div>

        {inviteCode && (
          <div className="success-panel">
            <p className="success-title">✅ Team created!</p>
            <p><strong>Team Code:</strong> <code>{teamId}</code></p>
            <p><strong>Invite Code (valid 24 hrs):</strong> <code>{inviteCode}</code></p>
            <div className="success-actions">
              <button className="ghost" onClick={copyToClipboard}>Copy invite</button>
              <button className="primary" onClick={goToDashboard}>Go to dashboard</button>
            </div>
          </div>
        )}

        <p className="back-text" onClick={() => navigate('/team-decision')}>
          ← Back to Team Options
        </p>
      </div>
    </div>
  );
};

export default CreateTeam;
